// TierComparePage — Visual side-by-side tier comparison
// Layout: 3-card tier picker on top, detailed feature matrix below, calculator

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierChip, TierPill } from '../../components/Brand';

const TIERS = [
  {
    key: 'Beginner', name: 'Beginner', emoji: '🌱', color: '#34C759', account: '$50K', monthly: 79, annual: 790, popular: false,
    perks: [
      { label: 'Account size',     val: '$50K',  included: true },
      { label: 'Activation fee',   val: '$79',   included: true },
      { label: 'Active bots',      val: '1',     included: true },
      { label: 'Strategies',       val: '4',     included: true },
      { label: 'Daily loss cap',   val: '5%',    included: true },
      { label: 'EOD drawdown',     val: '5%',    included: true },
      { label: 'Profit split',     val: '80%',   included: true },
      { label: 'Payouts',          val: 'Weekly', included: true },
      { label: 'AI bot',           val: 'Basic', included: true },
      { label: 'Live support',     val: 'Email', included: true },
      { label: 'Custom strategies',val: '—',     included: false },
      { label: 'API access',       val: '—',     included: false },
      { label: '1:1 coaching',     val: '—',     included: false },
    ],
  },
  {
    key: 'Mid', name: 'Mid', emoji: '🚀', color: '#007AFF', account: '$100K', monthly: 149, annual: 1490, popular: true,
    perks: [
      { label: 'Account size',     val: '$100K', included: true },
      { label: 'Activation fee',   val: '$149',  included: true },
      { label: 'Active bots',      val: '3',     included: true },
      { label: 'Strategies',       val: 'All 4', included: true },
      { label: 'Daily loss cap',   val: '5%',    included: true },
      { label: 'EOD drawdown',     val: '5%',    included: true },
      { label: 'Profit split',     val: '85%',   included: true },
      { label: 'Payouts',          val: '2x/week', included: true },
      { label: 'AI bot',           val: 'Pro',   included: true },
      { label: 'Live support',     val: 'Priority', included: true },
      { label: 'Custom strategies',val: '5',     included: true },
      { label: 'API access',       val: 'Read',  included: true },
      { label: '1:1 coaching',     val: '—',     included: false },
    ],
  },
  {
    key: 'Professional', name: 'Professional', emoji: '💎', color: '#1C1C1E', account: '$150K', monthly: 249, annual: 2490, popular: false,
    perks: [
      { label: 'Account size',     val: '$150K', included: true },
      { label: 'Activation fee',   val: '$249',  included: true },
      { label: 'Active bots',      val: '∞',     included: true },
      { label: 'Strategies',       val: 'All + custom', included: true },
      { label: 'Daily loss cap',   val: '5%',    included: true },
      { label: 'EOD drawdown',     val: '5%',    included: true },
      { label: 'Profit split',     val: '90-95%',included: true },
      { label: 'Payouts',          val: 'Daily', included: true },
      { label: 'AI bot',           val: 'Pro+',  included: true },
      { label: 'Live support',     val: '24/7',  included: true },
      { label: 'Custom strategies',val: '∞',     included: true },
      { label: 'API access',       val: 'Full',  included: true },
      { label: '1:1 coaching',     val: 'Monthly', included: true },
    ],
  },
];

export function TierComparePage() {
  const nav = useNavigate();
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const [highlight, setHighlight] = useState<'Beginner' | 'Mid' | 'Professional'>('Mid');
  const [calculator, setCalculator] = useState({ challenge: 5, winRate: 67, reward: 1000 });

  const tierData = (key: string) => TIERS.find((t) => t.key === key)!;
  const allPerks = TIERS[0].perks.map((_, i) => TIERS.map((t) => t.perks[i]));

  // Estimated monthly profit calculator
  const estMonthly = (tierKey: string) => {
    const splitPct = tierKey === 'Beginner' ? 0.80 : tierKey === 'Mid' ? 0.85 : 0.90;
    const tradesPerMonth = 100;
    const expectedReturn = (calculator.winRate / 100) * calculator.reward - ((100 - calculator.winRate) / 100) * (calculator.reward * 0.5);
    return (expectedReturn * tradesPerMonth * splitPct).toFixed(0);
  };

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Compare plans</div>
        <button onClick={() => nav('/portal/tier-checkout')} data-testid="upgrade-cta" className="px-3 text-[15px] font-semibold text-tf-blue">Upgrade</button>
      </div>

      {/* Hero */}
      <div className="mx-4 mt-3">
        <div className="rounded-[20px] border border-tf-border p-6 text-center" style={{ background: 'linear-gradient(135deg, #1C1C1E 0%, #5856D6 100%)', color: 'white' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">Choose your edge</div>
          <div className="mt-1 text-[24px] font-bold tracking-tight">Find the perfect tier</div>
          <div className="mt-1 text-[13px] text-white/85">All plans include 30-day money-back</div>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="mx-4 mt-3 ios26-seg" data-testid="billing">
        <button onClick={() => setBilling('monthly')} className={billing === 'monthly' ? 'active' : ''}>Monthly</button>
        <button onClick={() => setBilling('annual')} className={billing === 'annual' ? 'active' : ''}>
          Annual <span className="text-tf-profit">save 17%</span>
        </button>
      </div>

      {/* 3 tier cards */}
      <div className="mx-4 mt-3 grid grid-cols-3 gap-2">
        {TIERS.map((t) => (
          <button
            key={t.key}
            onClick={() => setHighlight(t.key as 'Beginner' | 'Mid' | 'Professional')}
            data-testid={`pick-${t.key}`}
            className={['relative rounded-[16px] p-3 text-left transition',
              highlight === t.key ? 'shadow-lg scale-[1.02]' : 'border border-tf-border bg-white'
            ].join(' ')}
            style={highlight === t.key ? { background: `linear-gradient(180deg, ${t.color}15 0%, white 100%)`, borderColor: t.color, borderWidth: 2 } : {}}
          >
            {t.popular && <span className="absolute -top-2 right-2 rounded-full bg-tf-warning px-1.5 py-0.5 text-[8px] font-bold uppercase text-white">Popular</span>}
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[20px]" style={{ backgroundColor: t.color + '20' }}>{t.emoji}</div>
            <div className="mt-1.5 text-[12px] font-bold text-tf-text-1">{t.name}</div>
            <div className="text-[9px] text-tf-text-3">{t.account} account</div>
            <div className="mt-1 text-[16px] font-bold tabular-nums text-tf-text-1">${billing === 'monthly' ? t.monthly : t.annual}</div>
            <div className="text-[9px] text-tf-text-3">/ {billing === 'monthly' ? 'mo' : 'yr'}</div>
          </button>
        ))}
      </div>

      {/* Visual feature matrix */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Features</h2>
      <div className="mx-4 overflow-hidden rounded-[16px] border border-tf-border bg-white">
        {allPerks.map((row, i) => (
          <div key={i} data-testid={`matrix-row-${i}`} className={['px-4 py-3', i < allPerks.length - 1 ? 'border-b border-tf-border' : ''].join(' ')}>
            <div className="text-[11px] font-semibold text-tf-text-3 uppercase tracking-wide mb-1.5">{row[0].label}</div>
            <div className="grid grid-cols-3 gap-1.5">
              {row.map((cell, j) => {
                const isHighlighted = highlight === TIERS[j].key;
                return (
                  <div
                    key={j}
                    className={['rounded-[10px] p-2 text-center transition',
                      cell.included ? (isHighlighted ? 'bg-tf-green-tint' : 'bg-tf-surface-1') : 'bg-tf-surface-1 opacity-60'
                    ].join(' ')}
                  >
                    {cell.included ? (
                      <>
                        <div className={['text-[12px] font-bold tabular-nums', isHighlighted ? 'text-tf-profit' : 'text-tf-text-1'].join(' ')}>{cell.val}</div>
                      </>
                    ) : (
                      <span className="text-tf-text-3 text-[14px]">—</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Visual bar comparison */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">Visual comparison</h2>
      <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-4">
        {/* Account size bars */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wide text-tf-text-3 mb-2">Account size</div>
          {[
            { tier: 'Beginner',     val: 50,  max: 150, color: '#34C759', display: '$50K' },
            { tier: 'Mid',          val: 100, max: 150, color: '#007AFF', display: '$100K' },
            { tier: 'Professional', val: 150, max: 150, color: '#1C1C1E', display: '$150K' },
          ].map((b) => (
            <div key={b.tier} className="mb-1.5 flex items-center gap-2">
              <div className="w-16 text-[10px] text-tf-text-3">{b.tier}</div>
              <div className="flex-1 h-5 overflow-hidden rounded-full bg-tf-surface-1">
                <div className="h-full rounded-full flex items-center justify-end pr-2 text-[10px] font-bold text-white" style={{ width: (b.val / b.max * 100) + '%', backgroundColor: b.color }}>{b.display}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Profit split bars */}
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-wide text-tf-text-3 mb-2">Profit split (max %)</div>
          {[
            { tier: 'Beginner',     val: 80,  color: '#34C759', display: '80%' },
            { tier: 'Mid',          val: 90,  color: '#007AFF', display: '90%' },
            { tier: 'Professional', val: 95,  color: '#1C1C1E', display: '95%' },
          ].map((b) => (
            <div key={b.tier} className="mb-1.5 flex items-center gap-2">
              <div className="w-16 text-[10px] text-tf-text-3">{b.tier}</div>
              <div className="flex-1 h-5 overflow-hidden rounded-full bg-tf-surface-1">
                <div className="h-full rounded-full flex items-center justify-end pr-2 text-[10px] font-bold text-white" style={{ width: b.val + '%', backgroundColor: b.color }}>{b.display}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Payout frequency */}
        <div>
          <div className="text-[11px] uppercase tracking-wide text-tf-text-3 mb-2">Payout frequency</div>
          {[
            { tier: 'Beginner',     val: 25, color: '#34C759', display: 'Weekly' },
            { tier: 'Mid',          val: 50, color: '#007AFF', display: '2x/wk' },
            { tier: 'Professional', val: 100, color: '#1C1C1E', display: 'Daily' },
          ].map((b) => (
            <div key={b.tier} className="mb-1.5 flex items-center gap-2">
              <div className="w-16 text-[10px] text-tf-text-3">{b.tier}</div>
              <div className="flex-1 h-5 overflow-hidden rounded-full bg-tf-surface-1">
                <div className="h-full rounded-full flex items-center justify-end pr-2 text-[10px] font-bold text-white" style={{ width: b.val + '%', backgroundColor: b.color }}>{b.display}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ROI Calculator */}
      <h2 className="mt-5 px-4 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">ROI calculator</h2>
      <div className="mx-4 rounded-[16px] border border-tf-border bg-white p-4">
        <p className="text-[12px] text-tf-text-3 mb-3">Adjust to estimate your monthly profit per tier</p>
        <div className="space-y-3">
          <SliderRow label="Trades per month" value={calculator.challenge} min={1} max={200} onChange={(v) => setCalculator({ ...calculator, challenge: v })} testid="roi-trades" />
          <SliderRow label="Win rate %"       value={calculator.winRate} min={40} max={90} onChange={(v) => setCalculator({ ...calculator, winRate: v })} testid="roi-win" />
          <SliderRow label="Avg reward $ "    value={calculator.reward}  min={50} max={5000} onChange={(v) => setCalculator({ ...calculator, reward: v })} testid="roi-reward" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {TIERS.map((t) => (
            <div key={t.key} data-testid={`roi-${t.key}`} className="rounded-[10px] p-2 text-center" style={{ backgroundColor: t.color + '15' }}>
              <div className="text-[9px] uppercase font-semibold" style={{ color: t.color }}>{t.name}</div>
              <div className="text-[15px] font-bold tabular-nums" style={{ color: t.color }}>${estMonthly(t.key)}</div>
              <div className="text-[8px] text-tf-text-3">/month</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="mx-4 mt-4">
        <button onClick={() => nav('/portal/tier-checkout')} data-testid="upgrade-cta-bottom" className="flex h-12 w-full items-center justify-center rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.98]">
          Upgrade to {highlight}
        </button>
      </div>
    </div>
  );
}

function SliderRow({ label, value, min, max, onChange, testid }: { label: string; value: number; min: number; max: number; onChange: (v: number) => void; testid?: string }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[12px] text-tf-text-1">{label}</span>
        <span className="text-[13px] font-bold tabular-nums text-tf-blue">{value}</span>
      </div>
      <input
        type="range"
        min={min} max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-[#007AFF] mt-1"
      />
    </div>
  );
}
